import { createAuthClient } from '@neondatabase/neon-js/auth';

import { AuthApiError } from './errors';
import { resolveNeonAuthUrl } from './neonAuthUrl';
import { safeReturnTo } from './returnTo';

export interface AuthAdapter {
  /**
   * Sends the browser to Google, then back to `returnTo` signed in. The page
   * unloads once the redirect starts, so this only settles on failure.
   */
  signInWithGoogle(returnTo: string): Promise<void>;
  /** The session JWT the API verifies, or null when nobody is signed in. */
  getToken(): Promise<string | null>;
  signOut(): Promise<void>;
}

function createNeonAuthAdapter(authUrl: string): AuthAdapter {
  const authClient = createAuthClient(authUrl);

  return {
    async signInWithGoogle(returnTo: string): Promise<void> {
      // Neon Auth appends its session verifier to this URL; getSession() picks it up on return.
      const callbackURL = new URL(safeReturnTo(returnTo), window.location.origin).href;
      const response = await authClient.signIn.social({ provider: 'google', callbackURL });
      throwIfNeonAuthError(response, 'Unable to start Google sign-in.');
    },
    async getToken(): Promise<string | null> {
      const response = await authClient.getSession();
      // No session is data: null; an error is Neon Auth being unreachable or refusing the request.
      throwIfNeonAuthError(response, 'Unable to read your session.');
      return response.data?.session?.token ?? null;
    },
    async signOut(): Promise<void> {
      const response = await authClient.signOut();
      throwIfNeonAuthError(response, 'Unable to sign out.');
    },
  };
}

/** Stands in when no Neon Auth URL reached the page, so the sign-in screen can say why it cannot work. */
const unconfiguredAdapter: AuthAdapter = {
  async signInWithGoogle(): Promise<void> {
    throw new AuthApiError(503, 'Sign-in is not configured for this environment.');
  },
  async getToken(): Promise<string | null> {
    return null;
  },
  async signOut(): Promise<void> {},
};

const neonAuthUrl = resolveNeonAuthUrl(
  typeof document === 'undefined' ? undefined : document,
  // `?.`: mounted tests import pages outside Vite, where import.meta has no env.
  import.meta.env?.VITE_NEON_AUTH_URL,
);

/** The branch's Neon Auth URL (its OAuth issuer), or undefined when none reached the page. */
export const neonAuthBaseUrl: string | undefined = neonAuthUrl?.replace(/\/+$/, '');

export const authAdapter: AuthAdapter =
  neonAuthUrl === undefined ? unconfiguredAdapter : createNeonAuthAdapter(neonAuthUrl);

function throwIfNeonAuthError(response: unknown, fallback: string): void {
  if (!isRecord(response) || response.error === null || response.error === undefined) {
    return;
  }

  const error = response.error;
  if (isRecord(error)) {
    const status = typeof error.status === 'number' && error.status > 0 ? error.status : 503;
    const message =
      typeof error.message === 'string' && error.message.length > 0 ? error.message : fallback;
    throw new AuthApiError(status, message);
  }

  throw new AuthApiError(503, fallback);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
